import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants, {PROCESS_TYPES} from 'constants/ModelTypeConstants';
import type {FilterTreeNode} from 'omnibox/omniboxFilterTree';
import {getModelTypeIconConfig} from 'omnibox/omniboxTypeIcons';
import type {OmniboxFilter} from 'omnibox/useTypeaheadData';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';
import {capwords, typeToName} from 'util/NameUtil';

/**
 * An option in the omnibox Type dropdown. `searchTypes` are the global search types (as in normal
 * search's Type filter) that selecting it narrows the omnibox search to; its schemas, from the
 * `schemaTypes` groups, nest beneath it. `children` are finer-grained options (e.g. the kinds of
 * Template).
 *
 * An `interfaceType` option (e.g. Antibody) is a kind of Custom Entity that search has no type of its
 * own for: it is offered when that type is searchable, searches Custom Entity, and is scoped to its
 * own schemas, so it narrows to those schemas and is hidden until they load.
 */
interface TypeOption {
  key: string;
  name?: string;
  searchTypes: ReadonlyArray<ModelTypeConstants>;
  schemaTypes?: ReadonlyArray<ModelTypeConstants>;
  interfaceType?: ModelTypeConstants;
  children?: ReadonlyArray<TypeOption>;
}

interface TypeSection {
  name: string;
  options: ReadonlyArray<TypeOption>;
}

function typeOption(
  modelType: ModelTypeConstants,
  overrides: Partial<Omit<TypeOption, 'key'>> = {}
): TypeOption {
  return {key: modelType, searchTypes: [modelType], ...overrides};
}

function interfaceOption(interfaceType: ModelTypeConstants): TypeOption {
  return {
    key: interfaceType,
    searchTypes: [ModelTypeConstants.BASIC_FOLDER_ITEM],
    schemaTypes: [interfaceType],
    interfaceType,
  };
}

const TEMPLATE_TYPES: ReadonlyArray<ModelTypeConstants> = [
  ModelTypeConstants.TEMPLATE,
  ModelTypeConstants.SUBTEMPLATE,
  ModelTypeConstants.FORM_DEFINITION,
  ModelTypeConstants.RECIPE_PROCEDURE,
  ModelTypeConstants.ASSAY_PROCEDURE,
  ModelTypeConstants.REQUEST_V2_DEFINITION,
  ModelTypeConstants.ANALYSIS_TEMPLATE,
  ModelTypeConstants.PLATE_DESIGN_TEMPLATE,
];

/**
 * Every type normal search's Type filter offers (TypesAndSchemaMenu), grouped by Benchling product
 * area. Within each section, and across sections where it allows, options follow that menu's order.
 * An option is shown only when its types are searchable (see buildTypeTreeNodes), which is how
 * feature and capability gating applies.
 */
const TYPE_SECTIONS: ReadonlyArray<TypeSection> = [
  {
    name: 'Projects',
    // Root folders are 1:1 with projects, so the folder type covers both, as in normal search.
    options: [typeOption(ModelTypeConstants.FOLDER, {name: 'Project or Folder'})],
  },
  {name: 'Studies', options: [typeOption(ModelTypeConstants.STUDY)]},
  {
    name: 'Registry',
    options: [
      interfaceOption(ModelTypeConstants.ANTIBODY),
      interfaceOption(ModelTypeConstants.OLIGO_CONJUGATE),
      interfaceOption(ModelTypeConstants.OLIGO_DUPLEX),
      interfaceOption(ModelTypeConstants.EQUIPMENT),
      typeOption(ModelTypeConstants.PROTEIN),
      typeOption(ModelTypeConstants.SEQUENCE),
      typeOption(ModelTypeConstants.RNA_SEQUENCE),
      typeOption(ModelTypeConstants.DNA_OLIGO),
      typeOption(ModelTypeConstants.RNA_OLIGO),
      typeOption(ModelTypeConstants.BASIC_FOLDER_ITEM),
      typeOption(ModelTypeConstants.MIXTURE),
      typeOption(ModelTypeConstants.MOLECULE),
      typeOption(ModelTypeConstants.REACTION, {name: 'Reaction'}),
    ],
  },
  {
    name: 'Notebook',
    options: [
      typeOption(ModelTypeConstants.ENTRY, {
        searchTypes: [ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY],
        schemaTypes: BenchlingConfig.ENABLE_ENTRY_SCHEMAS ? [ModelTypeConstants.ENTRY] : [],
      }),
      typeOption(ModelTypeConstants.TEMPLATE_COLLECTION),
      {
        // Every kind of template; each kind is also offered on its own beneath it.
        key: 'all_templates',
        name: 'Template',
        searchTypes: TEMPLATE_TYPES,
        schemaTypes: [],
        children: TEMPLATE_TYPES.map((type) => typeOption(type)),
      },
      typeOption(ModelTypeConstants.PROTOCOL),
    ],
  },
  {
    name: 'Workflows',
    options: [
      {
        key: 'task_group',
        name: 'Task group',
        searchTypes: PROCESS_TYPES,
        // The workflow task schemas, as in normal search. A schema picked here searches the task
        // groups of that schema, while the same schema under Workflow Task searches its tasks.
        schemaTypes: [ModelTypeConstants.PROCESS],
      },
      typeOption(ModelTypeConstants.PROCESS_TASK),
      typeOption(ModelTypeConstants.PROCESS_OUTPUT),
      typeOption(ModelTypeConstants.WORKFLOW),
    ],
  },
  {
    name: 'Requests',
    options: [typeOption(ModelTypeConstants.REQUEST_V2_SUBMISSION), typeOption(ModelTypeConstants.REQUEST)],
  },
  {
    name: 'Lab Automation',
    options: [typeOption(ModelTypeConstants.ASSAY_RUN), typeOption(ModelTypeConstants.CONNECTION)],
  },
  {
    name: 'Molecular Biology',
    options: [
      typeOption(ModelTypeConstants.BULK_ASSEMBLY),
      typeOption(ModelTypeConstants.SEQUENCE_ANALYSIS),
      typeOption(ModelTypeConstants.PROTEIN_ALIGNMENT),
    ],
  },
  {
    name: 'Insights',
    options: [
      typeOption(ModelTypeConstants.DATASET),
      typeOption(ModelTypeConstants.ANALYSIS),
      typeOption(ModelTypeConstants.ANALYTICS_DASHBOARD),
    ],
  },
  {
    name: 'Procedures',
    options: [
      typeOption(ModelTypeConstants.ASSAY_RUN_TASK),
      typeOption(ModelTypeConstants.ASSAY_UNIT_OPERATION_TASK),
      typeOption(ModelTypeConstants.ASSAY_STEP_TASK),
      typeOption(ModelTypeConstants.RECIPE_RUN_TASK),
      typeOption(ModelTypeConstants.RECIPE_UNIT_OPERATION_TASK),
      typeOption(ModelTypeConstants.RECIPE_STEP_TASK),
      typeOption(ModelTypeConstants.WORKSHEET),
    ],
  },
  {
    name: 'Inventory',
    options: [
      typeOption(ModelTypeConstants.CONTAINER),
      typeOption(ModelTypeConstants.PLATE),
      typeOption(ModelTypeConstants.BOX),
      typeOption(ModelTypeConstants.LOCATION),
    ],
  },
  {
    name: 'Sample Tests',
    options: [typeOption(ModelTypeConstants.TEST_DEFINITION), typeOption(ModelTypeConstants.TEST_ORDER)],
  },
  {
    name: 'Other',
    options: [
      typeOption(ModelTypeConstants.CANVAS_FOLDER_ARTIFACT),
      typeOption(ModelTypeConstants.FILE),
      typeOption(ModelTypeConstants.WORKLIST),
    ],
  },
];

/**
 * Builds the Type dropdown's options. An option is offered only for the search types in
 * `allowedSearchTypes` (the omnibox's gated searchable types), and narrows to just those; a
 * multi-type option covering a single allowed type is labeled as that type, as in normal search.
 * Schemas come from `schemasByType`, keyed by the type they belong to; `isTypeListed` gates the
 * interface-based types (Antibody and the like).
 */
export function buildTypeTreeNodes(
  allowedSearchTypes: ReadonlyArray<string>,
  schemasByType: ReadonlyMap<string, ReadonlyArray<OmniboxFilter>>,
  isTypeListed: (type: string) => boolean = isModelTypeEnabledForListing
): Array<FilterTreeNode> {
  const allowed = new Set(allowedSearchTypes);
  const nodes: Array<FilterTreeNode> = [];

  const addOption = (option: TypeOption, parentId: string | null, section: string | undefined): void => {
    const {interfaceType} = option;
    const searchTypes = option.searchTypes.filter((type) => allowed.has(type));
    // An interface type isn't a search type of its own, so it is gated as normal search's Type menu
    // gates it, on being listed, in addition to Custom Entity being searchable.
    if (searchTypes.length === 0 || (interfaceType != null && !isTypeListed(interfaceType))) {
      return;
    }
    // An interface option is scoped to its own schemas; with none loaded it would match every custom
    // entity, so it waits for them.
    const ownSchemas = interfaceType != null ? (schemasByType.get(interfaceType) ?? []) : [];
    if (interfaceType != null && ownSchemas.length === 0) {
      return;
    }
    const primaryType = searchTypes[0];
    const labelType = interfaceType ?? primaryType;
    const name =
      searchTypes.length === 1 && option.searchTypes.length > 1
        ? capwords(typeToName(primaryType))
        : (option.name ?? capwords(typeToName(labelType)));
    // Search results of an interface type are custom entities, so they take a custom entity's colors.
    const {bgColor, color} = getModelTypeIconConfig(primaryType, 'shapes-outline');
    const {icon} = getModelTypeIconConfig(labelType, 'shapes-outline');
    const id = `type:${option.key}`;
    nodes.push({
      id,
      parentId,
      name,
      icon,
      iconColors: {bgColor, color},
      filter: {
        kind: 'type',
        id: option.key,
        name,
        modelType: primaryType,
        searchTypes,
        ...(interfaceType != null && {schemaApiIds: ownSchemas.map((schema) => schema.id)}),
      },
      section,
    });
    for (const child of option.children ?? []) {
      addOption(child, id, undefined);
    }
    for (const schemaType of option.schemaTypes ?? option.searchTypes) {
      for (const schema of schemasByType.get(schemaType) ?? []) {
        nodes.push({
          id: `schema:${option.key}:${schema.id}`,
          parentId: id,
          name: schema.name,
          // The schema's own type icon (e.g. an antibody), in the colors of the option it's under.
          icon: getModelTypeIconConfig(schemaType, icon).icon,
          iconColors: {bgColor, color},
          filter: {...schema, modelType: primaryType, searchTypes, optionKey: option.key},
        });
      }
    }
  };

  for (const section of TYPE_SECTIONS) {
    for (const option of section.options) {
      addOption(option, null, section.name);
    }
  }
  return nodes;
}
